import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Dificultad, Modalidad, ParticipanteWeb, ParticipanteNoHit, ParticipanteStreamer, TipoPregunta } from '../types';
import { getApiUrl } from '../config';
import MainHeader from '../components/MainHeader';
import { useAuth } from '../contexts/AuthContext';

interface ResultsState {
  nickname: string;
  puntos: number;
  aciertos: number;
  totalPreguntas: number;
  dificultad: Dificultad;
  modalidad: Modalidad;
  respuestasPorTipo?: Record<TipoPregunta, number>;
  aciertosPorTipo?: Record<TipoPregunta, number>;
  falloFinalTipo?: TipoPregunta | null;
}

interface PersonalNoHitStats {
  recordNoHit: {
    racha: number;
    puntos: number;
    dificultad: Dificultad;
    fechaPartida: string;
  } | null;
  ultimasNoHit: Array<{
    aciertos: number;
    puntos: number;
    totalPreguntas: number;
    dificultad: Dificultad;
    fechaPartida: string;
  }>;
  tendenciaNoHit: 'subiendo' | 'estable' | 'bajando';
}

const Results: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ResultsState | null;
  const { user, loading: authLoading } = useAuth();

  const [guardado, setGuardado] = useState(false);
  const [posicion, setPosicion] = useState<number | null>(null);
  const [posicionNoHit, setPosicionNoHit] = useState<number | null>(null);
  const [totalPartidasNoHit, setTotalPartidasNoHit] = useState<number | null>(null);
  const [personalNoHitStats, setPersonalNoHitStats] = useState<PersonalNoHitStats | null>(null);
  const [rankingWeb, setRankingWeb] = useState<ParticipanteWeb[]>([]);
  const [rankingNoHit, setRankingNoHit] = useState<ParticipanteNoHit[]>([]);
  const [rankingStreamers, setRankingStreamers] = useState<ParticipanteStreamer[]>([]);
  const [cargando, setCargando] = useState(true);
  const filaJugadorStreamerRef = useRef<HTMLDivElement | null>(null);
  const hasSavedRef = useRef(false);

  useEffect(() => {
    if (!state) {
      navigate('/');
      return;
    }
    // Evita doble guardado por React StrictMode (doble montaje en desarrollo)
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;

    const guardarYCargar = async () => {
      try {
        setCargando(true);
        // Guardar resultado y obtener posición
        const { data: saved } = await axios.post(getApiUrl('/api/ranking/guardar'), {
          nickname: state.nickname,
          puntos: state.puntos,
          dificultad: state.dificultad,
          modalidad: state.modalidad,
          aciertos: state.aciertos,
          totalPreguntas: state.totalPreguntas,
          respuestasPorTipo: state.respuestasPorTipo,
          aciertosPorTipo: state.aciertosPorTipo,
          falloFinalTipo: state.falloFinalTipo,
        });
        setPosicion(saved.posicion);
        setPosicionNoHit(saved.posicionNoHit ?? null);
        setTotalPartidasNoHit(saved.totalPartidasNoHit ?? null);
        setGuardado(true);

        // Cargar rankings segun modalidad
        if (state.modalidad === 'nohit') {
          const [nohit, streamers] = await Promise.all([
            axios.get(getApiUrl('/api/ranking/nohit')),
            axios.get(getApiUrl('/api/ranking/streamers')),
          ]);
          setRankingNoHit(nohit.data);
          setRankingStreamers(streamers.data);
        } else {
          const [web, streamers] = await Promise.all([
            axios.get(getApiUrl('/api/ranking/web')),
            axios.get(getApiUrl('/api/ranking/streamers')),
          ]);
          setRankingWeb(web.data);
          setRankingStreamers(streamers.data);
        }
      } catch (e) {
        console.error('Error guardando resultado:', e);
        setGuardado(true); // Continuar aunque falle el guardado
      } finally {
        setCargando(false);
      }
    };

    guardarYCargar();
  }, []); // Solo al montar

  useEffect(() => {
    const cargarPersonalNoHit = async () => {
      if (!guardado || !state || state.modalidad !== 'nohit' || authLoading || !user) return;

      try {
        const { data } = await axios.get(getApiUrl('/api/ranking/mis-estadisticas'));
        setPersonalNoHitStats({
          recordNoHit: data.recordNoHit,
          ultimasNoHit: data.ultimasNoHit || [],
          tendenciaNoHit: data.tendenciaNoHit || 'estable',
        });
      } catch (error) {
        console.error('Error cargando estadísticas personales No Hit:', error);
      }
    };

    cargarPersonalNoHit();
  }, [guardado, state?.modalidad, authLoading, user]);

  if (!state) return null;

  const porcentaje = state.totalPreguntas > 0
    ? Math.round((state.aciertos / state.totalPreguntas) * 100)
    : 0;

  const getMedal = (pos: number) => {
    if (pos === 1) return '🥇';
    if (pos === 2) return '🥈';
    if (pos === 3) return '🥉';
    return `#${pos}`;
  };

  const getMedalColor = (pos: number) => {
    if (pos === 0) return 'text-yellow-400';
    if (pos === 1) return 'text-gray-300';
    if (pos === 2) return 'text-amber-600';
    return 'text-white/50';
  };

  const rankingStreamersConJugador = React.useMemo(() => {
    const yaExiste = rankingStreamers.some((s) =>
      (s.concursante || '').trim().toLowerCase() === state.nickname.trim().toLowerCase()
      || (s.nombreCC || '').trim().toLowerCase() === state.nickname.trim().toLowerCase()
    );

    const base = rankingStreamers.map((s) => ({
      ...s,
      esJugadorActual: false,
    }));

    if (!yaExiste) {
      base.push({
        concursante: state.nickname,
        nombreCC: '',
        puntos: state.puntos,
        dificultad: state.dificultad,
        fechaRegistro: new Date().toISOString(),
        esJugadorActual: true,
      });
    }

    return base
      .map((s) => ({
        ...s,
        esJugadorActual: s.esJugadorActual || (s.concursante || '').trim().toLowerCase() === state.nickname.trim().toLowerCase(),
      }))
      .sort((a, b) => b.puntos - a.puntos);
  }, [rankingStreamers, state.nickname, state.puntos, state.dificultad]);

  const rankingPrincipalTitle = state.modalidad === 'nohit' ? 'RANKING NO HIT' : 'TOP WEB PLAYERS';

  useEffect(() => {
    if (!cargando && filaJugadorStreamerRef.current) {
      filaJugadorStreamerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [cargando, rankingStreamersConJugador.length]);

  const getMensaje = () => {
    if (porcentaje === 100) return '¡PERFECTO! ¡Eres un DIOS del gaming!';
    if (porcentaje >= 80) return '¡INCREÍBLE! Gran nivel gamer';
    if (porcentaje >= 60) return '¡Muy bien! Sabes tus videojuegos';
    if (porcentaje >= 40) return 'No está mal, pero se puede mejorar';
    return 'Hay que jugar más videojuegos...';
  };

  const respuestasPorTipo = state.respuestasPorTipo || {
    trivia: 0,
    portada: 0,
    personaje: 0,
    cancion: 0,
  };

  const aciertosPorTipo = state.aciertosPorTipo || {
    trivia: 0,
    portada: 0,
    personaje: 0,
    cancion: 0,
  };

  const totalRespondidasPorTipo = Object.values(respuestasPorTipo).reduce((acc, value) => acc + value, 0);
  const recordNoHit = personalNoHitStats?.recordNoHit?.racha ?? null;
  const diferenciaConRecord = recordNoHit !== null ? state.aciertos - recordNoHit : null;
  const mejorQuePorcentaje = posicionNoHit && totalPartidasNoHit
    ? Math.max(0, Math.round(((totalPartidasNoHit - posicionNoHit) / totalPartidasNoHit) * 100))
    : null;
  const tendenciaNoHit = personalNoHitStats?.tendenciaNoHit ?? null;

  return (
    <div className="min-h-screen">
      <MainHeader />
      <div className="max-w-4xl mx-auto py-12 px-6">

        {/* Resultado principal */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="text-6xl mb-4">
            {state.modalidad === 'nohit' ? '💀' : porcentaje === 100 ? '🏆' : porcentaje >= 60 ? '🎮' : '👾'}
          </div>
          <h1 className="font-gaming text-2xl md:text-3xl text-white mb-3">
            {state.modalidad === 'nohit' ? 'GAME OVER' : 'PARTIDA TERMINADA'}
          </h1>
          <p className="font-main text-white/60 text-lg mb-8">{getMensaje()}</p>

          {/* Stats */}
          {state.modalidad === 'nohit' ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="card-gaming text-center">
                  <p className="font-main text-white/40 text-xs mb-1">RACHA FINAL</p>
                  <p className="font-gaming text-gaming-green text-2xl">{state.aciertos}</p>
                </div>
                <div className="card-gaming text-center">
                  <p className="font-main text-white/40 text-xs mb-1">POSICIÓN NO HIT</p>
                  <p className="font-gaming text-gaming-purple text-2xl">
                    {cargando ? '...' : posicionNoHit ? getMedal(posicionNoHit) : '-'}
                  </p>
                  <p className="font-main text-white/40 text-[11px] mt-2">
                    {mejorQuePorcentaje !== null ? `Mejor que ${mejorQuePorcentaje}%` : 'Sin ranking aún'}
                  </p>
                </div>
                <div className="card-gaming text-center">
                  <p className="font-main text-white/40 text-xs mb-1">RÉCORD PERSONAL</p>
                  <p className="font-gaming text-gaming-gold text-2xl">
                    {recordNoHit !== null ? recordNoHit : '-'}
                  </p>
                  <p className="font-main text-white/40 text-[11px] mt-2">
                    {diferenciaConRecord === null
                      ? 'Inicia sesión para verlo'
                      : diferenciaConRecord >= 0
                        ? 'Nuevo récord' 
                        : `Te faltaron ${Math.abs(diferenciaConRecord)} para tu récord`}
                  </p>
                </div>
                <div className="card-gaming text-center">
                  <p className="font-main text-white/40 text-xs mb-1">TENDENCIA</p>
                  <p className={`font-gaming text-2xl ${tendenciaNoHit === 'subiendo' ? 'text-gaming-green' : tendenciaNoHit === 'bajando' ? 'text-gaming-red' : 'text-gaming-cyan'}`}>
                    {tendenciaNoHit === 'subiendo' ? '↑' : tendenciaNoHit === 'bajando' ? '↓' : '→'}
                  </p>
                  <p className="font-main text-white/40 text-[11px] mt-2 capitalize">{tendenciaNoHit || 'estable'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="card-gaming">
                  <p className="font-gaming text-gaming-cyan text-xs mb-4">DESGLOSE POR TIPO</p>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      ['Trivia', respuestasPorTipo.trivia, aciertosPorTipo.trivia],
                      ['Portadas', respuestasPorTipo.portada, aciertosPorTipo.portada],
                      ['Personajes', respuestasPorTipo.personaje, aciertosPorTipo.personaje],
                      ['Canciones', respuestasPorTipo.cancion, aciertosPorTipo.cancion],
                    ] as Array<[string, number, number]>).map(([label, responded, hits]) => (
                      <div key={label} className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                        <p className="font-main text-white/50 text-[11px] uppercase tracking-widest mb-1">{label}</p>
                        <p className="font-gaming text-white text-lg">{responded}</p>
                      </div>
                    ))}
                  </div>
                  <p className="font-main text-white/40 text-xs mt-4">
                    Total respondidas: {totalRespondidasPorTipo}
                    {state.falloFinalTipo && (
                      <span className="ml-2">| Fallaste en {state.falloFinalTipo}</span>
                    )}
                  </p>
                </div>

                <div className="card-gaming">
                  <p className="font-gaming text-gaming-cyan text-xs mb-4">DETALLE DE LA RUN</p>
                  <div className="space-y-3 text-sm font-main text-white/70">
                    <div className="flex justify-between gap-4">
                      <span>Aciertos</span>
                      <span className="text-gaming-green font-gaming">{state.aciertos}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Precisión</span>
                      <span className="text-gaming-cyan font-gaming">{porcentaje}%</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Pregunta final</span>
                      <span className="text-white/80 font-gaming">#{state.totalPreguntas}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Contexto</span>
                      <span className="text-white/80 font-gaming">{mejorQuePorcentaje !== null ? `Top ${100 - mejorQuePorcentaje}%` : 'No disponible'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="card-gaming text-center">
                <p className="font-main text-white/40 text-xs mb-1">PUNTOS</p>
                <p className="font-gaming text-gaming-gold text-2xl">{state.puntos}</p>
              </div>
              <div className="card-gaming text-center">
                <p className="font-main text-white/40 text-xs mb-1">ACIERTOS</p>
                <p className="font-gaming text-gaming-green text-2xl">{state.aciertos}/{state.totalPreguntas}</p>
              </div>
              <div className="card-gaming text-center">
                <p className="font-main text-white/40 text-xs mb-1">PRECISIÓN</p>
                <p className="font-gaming text-gaming-cyan text-2xl">{porcentaje}%</p>
              </div>
              <div className="card-gaming text-center">
                <p className="font-main text-white/40 text-xs mb-1">POSICIÓN</p>
                <p className="font-gaming text-gaming-purple text-2xl">
                  {cargando ? '...' : posicion ? getMedal(posicion) : '-'}
                </p>
              </div>
            </div>
          )}

          {/* Modo y dificultad */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="bg-gaming-purple/20 border border-gaming-purple/40 text-gaming-purple font-main text-sm px-4 py-1 rounded-full">
              {state.modalidad === 'classic' ? 'Classic' : 'No Hit'}
            </span>
            <span className={`font-main text-sm px-4 py-1 rounded-full ${
              state.dificultad === 'dificil'
                ? 'bg-gaming-pink/20 border border-gaming-pink/40 text-gaming-pink'
                : 'bg-gaming-cyan/20 border border-gaming-cyan/40 text-gaming-cyan'
            }`}>
              {state.dificultad === 'dificil' ? 'Difícil' : 'Normal'}
            </span>
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/lobby')} className="btn-primary font-gaming text-xs tracking-wider py-4 px-8">
              JUGAR OTRA VEZ
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary font-gaming text-xs tracking-wider py-4 px-8">
              VOLVER AL INICIO
            </button>
          </div>
        </div>

        {/* Rankings lado a lado */}
        {!cargando && (
          <div className={`animate-slide-up ${state.modalidad === 'nohit' ? 'max-w-2xl mx-auto' : ''}`}>
            <div className={`grid grid-cols-1 ${state.modalidad === 'nohit' ? '' : 'md:grid-cols-2'} gap-6`}>
            {/* Ranking Web / No Hit */}
            <div className="card-gaming">
              <h3
                data-text={rankingPrincipalTitle}
                className="font-gaming text-gaming-cyan text-xs mb-4 tracking-widest ranking-neon-holo ranking-neon-holo--d"
              >
                {rankingPrincipalTitle}
              </h3>
              {state.modalidad === 'nohit' ? (
                rankingNoHit.length === 0 ? (
                  <p className="font-main text-white/40 text-sm">Sin datos</p>
                ) : (
                  <div className="space-y-2">
                    {rankingNoHit.map((p, i) => {
                      const esJugador = (p.nickname || '').trim().toLowerCase() === state.nickname.trim().toLowerCase();
                      return (
                        <div
                          key={p._id || `${p.nickname}-${i}`}
                          className={`flex items-center gap-3 p-2 rounded-lg ${
                            esJugador ? 'bg-gaming-purple/20 border border-gaming-purple/40' : ''
                          }`}
                        >
                          <span className="font-gaming text-xs w-6 text-center text-white/50">
                            {getMedal(i + 1)}
                          </span>
                          <span className={`font-main font-bold flex-1 text-sm ${esJugador ? 'text-gaming-purple' : 'text-white/80'}`}>
                            {p.nickname}
                            {esJugador && <span className="ml-2 text-xs text-gaming-purple/70">(tú)</span>}
                          </span>
                          <span className="font-gaming text-gaming-cyan text-xs">Racha {p.aciertos}</span>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                rankingWeb.length === 0 ? (
                  <p className="font-main text-white/40 text-sm">Sin datos</p>
                ) : (
                  <div className="space-y-2">
                    {rankingWeb.map((p, i) => {
                      const esJugador = p.nickname === state.nickname;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-2 rounded-lg ${
                            esJugador ? 'bg-gaming-purple/20 border border-gaming-purple/40' : ''
                          }`}
                        >
                          <span className="font-gaming text-xs w-6 text-center text-white/50">
                            {getMedal(i + 1)}
                          </span>
                          <span className={`font-main font-bold flex-1 text-sm ${esJugador ? 'text-gaming-purple' : 'text-white/80'}`}>
                            {p.nickname}
                            {esJugador && <span className="ml-2 text-xs text-gaming-purple/70">(tú)</span>}
                          </span>
                          <span className="font-gaming text-gaming-gold text-xs">{p.puntos}</span>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>

            {state.modalidad !== 'nohit' && (
              <div className="card-gaming">
                <h3
                  data-text="RANKING STREAMERS"
                  className="font-gaming text-gaming-cyan text-xs mb-4 tracking-widest ranking-neon-holo ranking-neon-holo--b"
                >
                  RANKING STREAMERS
                </h3>
                {rankingStreamersConJugador.length === 0 ? (
                  <p className="font-main text-white/40 text-sm">Sin datos</p>
                ) : (
                  <div className="space-y-3 overflow-y-auto max-h-[565px] pr-1">
                    {rankingStreamersConJugador.map((s, i) => (
                      <div
                        key={`${s.concursante}-${s.puntos}-${i}`}
                        ref={s.esJugadorActual ? filaJugadorStreamerRef : null}
                        className={`flex items-center gap-3 ${s.esJugadorActual ? 'bg-gaming-purple/15 border border-gaming-purple/40 rounded-lg px-2 py-1' : ''}`}
                      >
                        <span className={`font-gaming text-sm w-6 text-right ${getMedalColor(i)}`}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                        </span>

                        <div className="flex-1 min-w-0">
                          <p className={`font-main font-bold truncate ${s.esJugadorActual ? 'text-gaming-purple' : 'text-white'}`}>
                            {s.concursante}
                            {s.esJugadorActual && <span className="ml-2 text-xs text-gaming-purple/80">(tú)</span>}
                          </p>
                          {s.nombreCC && (
                            <p className="text-white/40 text-sm font-main truncate">{s.nombreCC.split(',')[0].replace(/\s*\([^)]*\)/g, '').trim()}</p>
                          )}
                        </div>

                        <div className="text-right shrink-0">
                          <p className="font-gaming text-gaming-gold text-xs">{s.puntos}</p>
                          <span className={s.dificultad === 'dificil' ? 'badge-dificil' : 'badge-normal'}>
                            {s.dificultad}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {cargando && (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-gaming-purple border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Results;
